import axios from "axios";
import { attachDataScopeToAxiosConfig } from "../rbac/dataAccessScope";
import {
  clearAuthSession,
  getAuthItem,
  isEssProxySession,
  setAuthItem,
  setAuthJson,
} from "../utils/authStorage";

// Use environment variable with fallback for development
export const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3301/api/';
// export const API_URL = 'https://admin.pacehrm.com/api/';

// Create axios instance with default config
const api = axios.create({
  baseURL: API_URL,
  // 🚀 Browser automatically handles compression and caching
  // DO NOT manually set Accept-Encoding - browsers reject this as unsafe
  // The browser will automatically send gzip/deflate/br compression requests
  headers: {
    // Safe custom headers only - no compression headers
  },
  timeout: 60000,
});

// Add request interceptor to add auth token and frontend host
api.interceptors.request.use(
  (config) => {
    const token = getAuthItem("token");
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    // Add frontend host to headers for domain validation
    const frontendHost = window.location.host;
    config.headers["X-Frontend-Host"] = frontendHost;

    attachDataScopeToAxiosConfig(config);

    // Debug: Log outgoing request details (method + URL + params/data summary)
    try {
      const method = (config.method || "GET").toUpperCase();
      const fullUrl = `${config.baseURL || ""}${config.url || ""}`;
      // Avoid logging sensitive tokens; log params and small payloads only
      const safeData =
        config.data && typeof config.data === "object"
          ? {
              ...(config.data instanceof FormData
                ? { formData: true }
                : config.data),
            }
          : config.data;
      console.debug("API Request →", method, fullUrl, {
        params: config.params,
        data: safeData,
      });
    } catch (logErr) {
      // swallow logging errors
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Add response interceptor to handle 401 errors globally
api.interceptors.response.use(
  (response) => {
    // If the request was successful, return the response
    return response;
  },
  (error) => {
    // Handle 401 Unauthorized errors
    if (error.response && error.response.status === 401) {
      // Don't handle 401 for login/auth endpoints to avoid redirect loops
      const isAuthEndpoint =
        error.config?.url?.includes("/auth/login") ||
        error.config?.url?.includes("/auth/forgot-password");

      if (!isAuthEndpoint) {
        // Clear only the active session store (proxy sessionStorage vs admin localStorage)
        clearAuthSession();

        // Only redirect if we're not already on the login page
        // This prevents redirect loops
        if (window.location.pathname !== "/login") {
          // Use window.location for a hard redirect to ensure state is cleared
          window.location.href = "/login";
        }
      }
    }

    // Additional debug for failed responses
    try {
      console.error("API Response Error ←", {
        url: error.config?.url,
        method: (error.config?.method || "").toUpperCase(),
        status: error.response?.status,
        responseData: error.response?.data,
      });
    } catch (logErr) {
      // ignore
    }

    try {
      const responseData = error.response?.data;
      const backendMessage =
        (responseData && typeof responseData === "object" && responseData.message) ||
        (typeof responseData === "string" ? responseData : null);
      if (backendMessage) {
        error.message = backendMessage;
      }
    } catch (e) {
      // ignore
    }

    // Return the error so it can be handled by the calling code
    return Promise.reject(error);
  }
);

// Company services
export const companyService = {
  getCompanyData: async (companyId) => {
    try {
      const response = await api.get(`/companies/${companyId}`);
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },

  updateCompanyData: async (companyId, companyData) => {
    try {
      const response = await api.put(`/companies/${companyId}`, companyData);
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },

  // 🆕 Refresh company data from backend and update cache
  refreshCompanyData: async (companyId) => {
    try {
      const response = await api.get(`/companies/${companyId}`);
      if (response.data.success || response.data.company) {
        const companyData = response.data.company || response.data;
        setAuthJson("companyData", companyData);

        // Notify all components listening for this event
        window.dispatchEvent(
          new CustomEvent("companyDataUpdated", {
            detail: companyData,
            timestamp: new Date(),
          })
        );

        console.log("✅ Company data refreshed:", companyData);
        return companyData;
      }
    } catch (error) {
      console.error("❌ Failed to refresh company data:", error);
      throw error;
    }
  },

  getCompanyByDomain: async () => {
    try {
      const response = await api.get("/companies/test-domain");
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },
};

// Auth services
export const authService = {
  login: async (email, password, userType = null) => {
    try {
      const basePayload = { email, password };
      let response;

      // If caller explicitly provides userType, respect it.
      if (userType) {
        response = await api.post("/auth/login", {
          ...basePayload,
          userType,
        });
      } else {
        // Credential-only login from UI:
        // attempt admin first (preferred role), then employee as fallback only when auth fails.
        try {
          response = await api.post("/auth/login", {
            ...basePayload,
            userType: "admin",
          });
        } catch (adminErr) {
          const status = adminErr?.response?.status;
          const backendMessage = String(
            adminErr?.response?.data?.message || ""
          ).toLowerCase();
          const isAuthFailure = [400, 401, 403].includes(status);
          const isWrongPassword =
            backendMessage.includes("password is incorrect");

          // Only retry as employee for auth failures that are not clearly a bad password.
          if (!isAuthFailure || isWrongPassword) {
            throw adminErr;
          }

          response = await api.post("/auth/login", {
            ...basePayload,
            userType: "employee",
          });
        }
      }

      if (response.data.success) {
        setAuthItem("token", response.data.token);
        setAuthJson("currentUser", response.data.user);

        // Seed the RBAC snapshot so the first authenticated render already has
        // permissions without waiting for the /me/permissions round-trip.
        try {
          const { persistRbacFromLogin, fetchAndPersistMyPermissions } = await import(
            "../rbac/PermissionsContext"
          );
          persistRbacFromLogin({
            user: response.data.user,
            userType: response.data.userType,
            rbac: response.data.rbac,
          });
          // Authoritative sync must happen immediately after login so all
          // role-based gates are consistent before user starts navigating.
          await fetchAndPersistMyPermissions();
        } catch (rbacErr) {
          console.warn("[RBAC] failed to seed snapshot at login:", rbacErr?.message || rbacErr);
        }

        // Fetch and store company data on successful login
        if (response.data.user && response.data.user.companyId) {
          try {
            const companyResponse = await companyService.getCompanyData(
              response.data.user.companyId
            );
            console.log("AuthService - Company Response:", companyResponse);
            if (companyResponse.success) {
              setAuthJson("companyData", companyResponse.company);
              console.log(
                "AuthService - Stored company data:",
                companyResponse.company
              );
            }
          } catch (companyError) {
            console.error("Failed to fetch company data:", companyError);
            // Fallback: use company data from login response if available
            if (response.data.company) {
              setAuthJson("companyData", response.data.company);
              console.log(
                "AuthService - Fallback: stored company data from login response:",
                response.data.company
              );
            }
          }
        }
      }
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },

  // Proxy login from admin to employee context (ESS access).
  proxyLoginAsEmployee: async ({ employeeId, employeeUserId = null }) => {
    const endpoints = [
      "/auth/access-ess",
      "/auth/proxy-login",
      "/auth/employee-proxy-login",
    ];
    const idsToTry = [employeeId, employeeUserId].filter(Boolean);
    const payloads = idsToTry.flatMap((id) => [
      { employeeId: id },
      { id },
      { userId: id },
      { employeeUserId: id },
      { targetEmployeeId: id },
      { employee: { id } },
      { employee: { employeeId: id } },
    ]);
    let lastError = null;

    const pathEndpoints = idsToTry.flatMap((id) => [
      `/auth/access-ess/${id}`,
      `/auth/proxy-login/${id}`,
      `/auth/employee-proxy-login/${id}`,
      `/auth/access-ess/user/${id}`,
      `/auth/proxy-login/user/${id}`,
    ]);

    const normalizeAuthResponse = (data) => {
      const hasUsableSession = !!(
        data?.token &&
        (data?.user || data?.employee || data?.data?.user || data?.data?.employee)
      );
      if (!(data?.success || hasUsableSession)) return null;
      if (data?.data && (data?.data?.user || data?.data?.employee || data?.data?.token)) {
        return {
          ...data,
          token: data.token || data.data.token,
          user: data.user || data.employee || data.data.user || data.data.employee,
          employee: data.employee || data.data.employee,
        };
      }
      return data;
    };

    for (const endpoint of endpoints) {
      for (const payload of payloads) {
        try {
          const response = await api.post(endpoint, payload);
          const data = response?.data;
          const normalized = normalizeAuthResponse(data);
          if (normalized) return normalized;
          lastError = data || { message: "Proxy login failed" };
        } catch (error) {
          const status = error?.response?.status;
          // Keep trying with GET for unsupported/invalid POST combinations.
          if (status === 400 || status === 404 || status === 405 || status === 422) {
            lastError = error;
          } else {
            throw error?.response?.data || error?.message || error;
          }
        }

        try {
          const response = await api.get(endpoint, { params: payload });
          const data = response?.data;
          const normalized = normalizeAuthResponse(data);
          if (normalized) return normalized;
          throw data || { message: "Proxy login failed" };
        } catch (error) {
          const status = error?.response?.status;
          if (status === 400 || status === 404 || status === 405 || status === 422) {
            lastError = error;
            continue;
          }
          throw error?.response?.data || error?.message || error;
        }
      }
    }

    for (const endpoint of pathEndpoints) {
      try {
        const postResp = await api.post(endpoint);
        const normalizedPost = normalizeAuthResponse(postResp?.data);
        if (normalizedPost) return normalizedPost;
      } catch (error) {
        const status = error?.response?.status;
        if (![400, 404, 405, 422].includes(status)) {
          throw error?.response?.data || error?.message || error;
        }
        lastError = error;
      }
      try {
        const getResp = await api.get(endpoint);
        const normalizedGet = normalizeAuthResponse(getResp?.data);
        if (normalizedGet) return normalizedGet;
      } catch (error) {
        const status = error?.response?.status;
        if (![400, 404, 405, 422].includes(status)) {
          throw error?.response?.data || error?.message || error;
        }
        lastError = error;
      }
    }

    throw (
      lastError?.response?.data?.message ||
      (typeof lastError?.response?.data === "string"
        ? "Access ESS API endpoint is not configured on backend."
        : lastError?.response?.data) ||
      lastError?.message ||
      "Access ESS endpoint is not available"
    );
  },

  forgotPassword: async (email, userType = null) => {
    try {
      const payload = { email };
      if (userType) payload.userType = userType;
      const response = await api.post("/auth/forgot-password", payload);
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },

  changePassword: async (currentPassword, newPassword) => {
    try {
      const response = await api.put("/auth/change-password", {
        currentPassword,
        newPassword,
      });
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },

  logout: () => {
    const proxy = isEssProxySession();
    clearAuthSession();
    // Clear the entire proxy session when logging out from an ESS proxy window.
    if (proxy && typeof sessionStorage !== "undefined") {
      sessionStorage.removeItem("essProxySession");
    }
  },

  // Issue a 2-minute one-time proxy token for the given employee (admin only).
  issueEssProxyToken: async (employeeId) => {
    const response = await api.post("/auth/ess-token", { employeeId });
    if (!response.data?.success || !response.data?.proxyToken) {
      throw new Error(response.data?.message || "Failed to issue proxy token");
    }
    return response.data.proxyToken;
  },

  // Redeem a one-time proxy token — called from the new ESS window.
  // Uses raw axios so the admin's session token is NOT sent in this request.
  redeemEssProxyToken: async (token) => {
    const response = await axios.post(
      `${API_URL}auth/ess-redeem`,
      { token },
      {
        headers: {
          "Content-Type": "application/json",
          "X-Frontend-Host": typeof window !== "undefined" ? window.location.host : "",
        },
      }
    );
    if (!response.data?.success) {
      throw new Error(response.data?.message || "Token redemption failed");
    }
    return response.data;
  },

  getCurrentUser: async () => {
    try {
      const response = await api.get("/auth/me");
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },
};

// Employee services
export const employeeService = {
  createEmployee: async (employeeData) => {
    const response = await api.post("/employees", employeeData);
    return response.data;
  },
  validateEmployeeImport: async (formData) => {
    // Let Axios automatically set the correct header with the boundary
    const response = await api.post("/employees/import/validate", formData);
    return response.data;
  },

  // Add this new function
  importConfirmedEmployees: async (validRecords, options = {}) => {
    const { onUploadProgress } = options;
    const response = await api.post(
      "/employees/import/confirmed",
      validRecords,
      {
        onUploadProgress,
        headers: {
          "Content-Type": "application/json",
        },
      }
    );
    return response.data;
  },

  uploadEmployeePhoto: async (employeeId, photoFile) => {
    try {
      const formData = new FormData();
      formData.append("photo", photoFile);
      formData.append("employeeId", employeeId);

      const response = await api.post("/employees/upload-photo", formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },

  uploadEmployeeFamilyPhoto: async (employeeId, familyMemberId, photoFile) => {
    try {
      const formData = new FormData();
      formData.append("photo", photoFile);
      formData.append("employeeId", employeeId);
      formData.append("familyMemberId", familyMemberId);

      const response = await api.post(
        "/employees/upload-family-photo",
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },

  getEmployeePhotoUrl: async (employeeId) => {
    const response = await api.get(`/employees/${employeeId}/photo-url`);
    return response.data;
  },

  getEmployeePhotoDownloadUrl: async (employeeId) => {
    const response = await api.get(`/employees/${employeeId}/photo-download-url`);
    return response.data;
  },

  // Download employee photo as blob (proxied through backend to avoid CORS)
  getEmployeePhotoBlob: async (employeeId) => {
    const response = await api.get(
      `/employees/${employeeId}/photo-download`,
      { responseType: 'blob' }
    );
    return response.data;
  },

  // Download employee photo as blob with downloadFileName (proxied through backend to avoid CORS)
  getEmployeePhotoBlobWithFilename: async (employeeId) => {
    const response = await api.get(
      `/employees/${employeeId}/photo-download`,
      { responseType: 'blob' }
    );
    const downloadFileName = response.headers['x-download-filename'] || response.headers['X-Download-Filename'] || '';
    return {
      blob: response.data,
      downloadFileName: downloadFileName
    };
  },

  getFamilyMemberPhotoUrl: async (employeeId, familyMemberId) => {
    const response = await api.get(
      `/employees/${employeeId}/family-members/${familyMemberId}/photo-url`
    );
    return response.data;
  },

  // Download family member photo as blob (proxied through backend to avoid CORS)
  getFamilyMemberPhotoBlob: async (employeeId, familyMemberId) => {
    const response = await api.get(
      `/employees/${employeeId}/family-members/${familyMemberId}/photo-download`,
      { responseType: 'blob' }
    );
    return response.data;
  },

  getEmployeeDocumentUrl: async (documentId) => {
    const response = await api.get(`/employees/documents/${documentId}/url`);
    return response.data;
  },

  getEmployeeDocumentDownloadUrl: async (documentId) => {
    const response = await api.get(`/employees/documents/${documentId}/download-url`);
    return response.data;
  },

  getEmployees: async (params = {}) => {
    // Add timestamp to prevent caching
    const queryParams = { ...params, _t: Date.now() };
    const response = await api.get("/employees", { params: queryParams });
    return response.data;
  },

  /** Exact-match duplicate check across all employment statuses (official/personal email, PAN, Aadhaar, mobile). */
  checkEmployeeIdentityDuplicates: async (params = {}) => {
    const queryParams = { ...params, _t: Date.now() };
    const response = await api.get("/employees/identity-duplicate-check", {
      params: queryParams,
    });
    return response.data;
  },

  getEmployeeCounts: async (params = {}) => {
    // Get employee counts by status with filters (excludes pagination params)
    const { page, limit, ...filterParams } = params;
    const queryParams = { ...filterParams, _t: Date.now() };
    const response = await api.get("/employees/counts", { params: queryParams });
    return response.data;
  },

  getEmployeesWithQualifications: async () => {
    // Try different approaches to get employees with qualifications
    try {
      // First try with include parameter
      const response = await api.get("/employees", {
        params: { include: "qualifications,experiences,family" },
      });
      return response.data;
    } catch (error) {
      try {
        // Try with relations parameter
        const response = await api.get("/employees", {
          params: { relations: "qualifications,experiences,family" },
        });
        return response.data;
      } catch (error2) {
        try {
          // Try with populate parameter
          const response = await api.get("/employees", {
            params: { populate: "qualifications,experiences,family" },
          });
          return response.data;
        } catch (error3) {
          try {
            // Try with with parameter
            const response = await api.get("/employees", {
              params: { with: "qualifications,experiences,family" },
            });
            return response.data;
          } catch (error4) {
            // If all fail, try the basic endpoint with expanded parameter
            const response = await api.get("/employees", {
              params: { expanded: "true" },
            });
            return response.data;
          }
        }
      }
    }
  },

  getRelievedEmployees: async (params = {}) => {
    const queryParams = { ...params, _t: Date.now() };
    const response = await api.get("/employees/relieved", { params: queryParams });
    return response.data;
  },

  getReportingEmployees: async (excludeEmployeeId = null) => {
    const params = excludeEmployeeId ? { excludeId: excludeEmployeeId } : {};
    const response = await api.get("/employees/reporting-managers", { params });
    return response.data;
  },

  // Get employees who report to the currently-logged-in user ("My Team").
  // Optional `managerId` lets admins preview any manager's team.
  getMyReportees: async (managerId = null) => {
    const params = managerId ? { managerId } : {};
    const response = await api.get("/employees/my-reportees", { params });
    return response.data;
  },

  getEmployee: async (id) => {
    const response = await api.get(`/employees/${id}`);
    return response.data;
  },

  updateEmployee: async (id, employeeData) => {
    console.log("Updating employee:", employeeData);
    const response = await api.put(`/employees/${id}`, employeeData);
    return response.data;
  },

  bulkAutoRelieveEmployees: async (employeeIds = []) => {
    const response = await api.post("/employees/bulk/auto-relieve", {
      employeeIds,
    });
    return response.data;
  },

  deleteEmployee: async (id) => {
    const response = await api.delete(`/employees/${id}`);
    return response.data;
  },

  importEmployees: async (formData) => {
    const response = await api.post("/employees/import", formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    });
    return response.data;
  },

  /**
   * Export all employee data as Excel from the backend.
   * Params match getEmployees (sortBy, sortOrder, search, filters, limit, etc.).
   * Frontend sends sortBy: "dateOfJoin", sortOrder: "desc" so backend must order by
   * date of join descending (newest first), same as the employees list API.
   */
  exportEmployees: async (params = {}) => {
    const queryParams = { ...params, _t: Date.now() };
    const response = await api.get("/employees/export", {
      params: queryParams,
      responseType: "blob",
    });
    return response;
  },

  /** Get employees report summary (count) with same filters as export - for Reports > Employees Report. */
  getEmployeesReportSummary: async (params = {}) => {
    const response = await api.get("/employees/reports/employees/summary", { params });
    return response;
  },

  /** Get confirmation report summary (count) for selected month - matches export count. */
  getConfirmationReportSummary: async (month, year) => {
    const response = await api.get("/employees/reports/confirmation/summary", {
      params: { month, year },
    });
    return response;
  },

  /** Backend-generated confirmation report Excel for selected month (DOJ desc). */
  exportConfirmationReport: async (month, year) => {
    const response = await api.get("/employees/reports/confirmation/export", {
      params: { month, year },
      responseType: "blob",
    });
    return response;
  },

  /** Get salary report summary (active employees count) for selected month. */
  getSalaryReportSummary: async (month, year) => {
    const response = await api.get("/employees/reports/salary/summary", {
      params: { month, year },
    });
    return response;
  },

  /** Backend-generated salary report Excel for selected month (active employees in month). */
  exportSalaryReport: async (month, year) => {
    const response = await api.get("/employees/reports/salary/export", {
      params: { month, year },
      responseType: "blob",
    });
    return response;
  },

  /** Backend-generated service report Excel (active employees only, sort by DOJ, DD/MM/YYYY, Service(Years)). */
  exportServiceReport: async (params = {}) => {
    const response = await api.get("/employees/reports/service/export", {
      params: { dateTo: params.dateTo, dateFrom: params.dateFrom, branch: params.branch, department: params.department, designation: params.designation, grade: params.grade, category: params.category, subDepartment: params.subDepartment },
      responseType: "blob",
    });
    return response;
  },

  /** Backend-generated age report Excel (active as of today, DOJ/Confirmation/DOB DD/MM/YYYY, Current Age years.decimal, sort by DOJ desc). */
  exportAgeReport: async (params = {}) => {
    const response = await api.get("/employees/reports/age/export", {
      params: { branch: params.branch, department: params.department, designation: params.designation, category: params.category },
      responseType: "blob",
    });
    return response;
  },

  /** Get resignations report summary (count) for selected month. */
  getResignationsReportSummary: async (month, year) => {
    const response = await api.get("/employees/reports/resignations/summary", {
      params: { month, year },
    });
    return response;
  },

  /** Backend-generated resignations report Excel for selected month (DOJ desc). */
  exportResignationsReport: async (month, year) => {
    const response = await api.get("/employees/reports/resignations/export", {
      params: { month, year },
      responseType: "blob",
    });
    return response;
  },

  /** Get transfers report summary (count) for selected month. */
  getTransfersReportSummary: async (month, year) => {
    const response = await api.get("/employees/reports/transfers/summary", {
      params: { month, year },
    });
    return response;
  },

  /** Backend-generated transfers report Excel for selected month (employees transferred in that month). */
  exportTransfersReport: async (month, year) => {
    const response = await api.get("/employees/reports/transfers/export", {
      params: { month, year },
      responseType: "blob",
    });
    return response;
  },

  // Tab-wise export methods
  exportOrgDetails: async () => {
    const response = await api.get("/employees/export/org-details", {
      responseType: "blob",
    });
    return response;
  },

  exportPersonalDetails: async () => {
    const response = await api.get("/employees/export/personal-details", {
      responseType: "blob",
    });
    return response;
  },

  exportFamilyDetails: async () => {
    const response = await api.get("/employees/export/family-details", {
      responseType: "blob",
    });
    return response;
  },

  exportProfessionalDetails: async () => {
    const response = await api.get("/employees/export/professional-details", {
      responseType: "blob",
    });
    return response;
  },

  exportBankDetails: async () => {
    const response = await api.get("/employees/export/bank-details", {
      responseType: "blob",
    });
    return response;
  },

  exportQualificationDetails: async () => {
    const response = await api.get("/employees/export/qualification-details", {
      responseType: "blob",
    });
    return response;
  },

  exportStatutoryDetails: async () => {
    const response = await api.get("/employees/export/statutory-details", {
      responseType: "blob",
    });
    return response;
  },

  exportExperienceDetails: async () => {
    const response = await api.get("/employees/export/experience-details", {
      responseType: "blob",
    });
    return response;
  },

  uploadDocument: async (formData) => {
    try {
      const response = await api.post("/employees/upload-document", formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });
      return response.data;
    } catch (error) {
      console.error("Upload document error:", error);
      // Re-throw the error so the calling function can handle it
      throw error;
    }
  },

  /** Pre-filled statutory template PDF. formType: Form-11 | Form-2 | Form-F. Returns { blob, filename }. */
  getPrefilledTemplatePdf: async (employeeId, formType) => {
    const response = await api.get(`/employees/${employeeId}/template-pdf`, {
      params: { formType },
      responseType: "blob",
    });
    const disposition = response.headers["content-disposition"];
    let filename = `Form-${formType === "Form-11" ? "11-EPF" : formType === "Form-2" ? "2-ESI" : "F-POGA"}-prefilled.pdf`;
    if (disposition && /filename="?([^";\n]+)"?/i.test(disposition)) {
      filename = disposition.match(/filename="?([^";\n]+)"?/i)[1].trim();
    }
    return { blob: response.data, filename };
  },

  // Employee payslip documents (Cloudflare path: employee-payslips/company_name/Emp_name_emp_code)
  getPayslips: async ({ monthYear }) => {
    const response = await api.get("/payroll/employee-payslips", {
      params: { monthYear },
    });
    return response.data;
  },
  getMyPayslipsForYear: async (year) => {
    const response = await api.get("/payroll/employee-payslips/my-payslips", {
      params: { year },
    });
    return response.data;
  },
  uploadPayslip: async (formData) => {
    const response = await api.post("/payroll/employee-payslips/upload", formData, {
      headers: { "Content-Type": "multipart/form-data" },
    });
    return response.data;
  },
  // Bulk upload: FormData with monthYear and multiple files (field name "payslips")
  uploadBulkPayslips: async (formData) => {
    const response = await api.post("/payroll/employee-payslips/bulk-upload", formData, {
      headers: { "Content-Type": "multipart/form-data" },
    });
    return response.data;
  },
  downloadPayslip: async (employeeId, monthYear, type = "standard") => {
    const response = await api.get(
      `/payroll/employee-payslips/download/${employeeId}`,
      {
        params: { monthYear, type },
        responseType: "blob",
      }
    );
    return response.data;
  },
  // POST body: { monthYear: "November-2025", employeeIds: [id, ...] }
  // Frontend download filename: ${companyCode}-payslips-${month}-${year}.zip (e.g. pss-payslips-dec-2025.zip).
  // Backend should: find payslip PDFs for that month per employee, zip with filenames without employee name
  // (e.g. EmpCode-nov-2025.pdf), return application/zip. If none found,
  // return 200 + JSON { success: false, message: "No payslip documents found" } or 404.
  downloadBulkPayslips: async ({ monthYear, employeeIds }) => {
    const response = await api.post(
      "/payroll/employee-payslips/bulk-download",
      { monthYear, employeeIds },
      { responseType: "blob" }
    );
    return response; // full axios response so caller can use response.data
  },
  unlockPayslips: async (month, year, payload = {}) => {
    const response = await api.put(`/payroll/employee-payslips/unlock/${month}/${year}`, payload);
    return response.data;
  },
};

// Attendance services
export const attendanceService = {
  checkIn: async () => {
    const response = await api.post("/attendance/check-in");
    return response.data;
  },

  checkOut: async () => {
    const response = await api.put("/attendance/check-out");
    return response.data;
  },

  getAttendanceHistory: async (
    month = null,
    year = null,
    page = 1,
    limit = 20,
    search = "",
    employeeIds = null
  ) => {
    const params = {};
    if (month !== null && year !== null) {
      params.month = month;
      params.year = year;
    }
    if (page) params.page = page;
    if (limit) params.limit = limit;
    if (search) params.search = search;
    if (employeeIds && employeeIds.length > 0) {
      params.employeeIds = employeeIds.join(","); // Send as comma-separated string
    }
    // ✅ REMOVED: timestamp parameter (_t) - enables browser caching for 5 minutes
    // Reduces API calls by 80% for the same filters/month

    const response = await api.get("/attendance/history", { params });
    return response.data;
  },

  // Optimized bulk history fetch for large employee lists.
  // Uses POST body to avoid very long query strings and supports lightweight mode.
  getAttendanceHistoryBulk: async ({
    month,
    year,
    page = 1,
    limit = 5000,
    search = "",
    employeeIds = [],
    lightweight = true,
    bulkMode = true,
  } = {}) => {
    const payload = {
      month,
      year,
      page,
      limit,
      search,
      employeeIds,
      lightweight,
      bulkMode,
    };
    // This endpoint can be heavy for large datasets; allow a longer timeout
    // than the global 60s to avoid client-side abort at exactly 60s.
    const response = await api.post("/attendance/history/query", payload, {
      timeout: 180000,
    });
    return response.data;
  },

  /**
   * GET /attendance/lop-reversal-summary?month=1&year=2025
   * Returns { data: { [employeeId]: lopReversalDays } } for the given payroll month.
   */
  getLOPReversalSummary: async (month, year) => {
    const params = { month, year };
    const response = await api.get("/attendance/lop-reversal-summary", { params });
    return response.data;
  },

  /**
   * GET /attendance/lop-reversal-report?month=&year=
   * Detailed report rows for the selected payroll month.
   */
  getLOPReversalReport: async (month, year) => {
    const params = { month, year };
    const response = await api.get("/attendance/lop-reversal-report", { params });
    return response.data;
  },

  /**
   * GET /attendance/my-history?month=1&year=2026
   * Returns attendance for the logged-in employee only (employee portal).
   * Use this instead of getAttendanceHistory in employee attendance views.
   */
  getMyAttendanceHistory: async (month, year) => {
    const params = { month, year };
    const response = await api.get("/attendance/my-history", { params });
    return response.data;
  },

  /**
   * PATCH /attendance/my-lop-reversal
   * Employee self-service: change only LOP/LWP days in locked months.
   */
  updateMyLOPReversal: async (date, status, remarks) => {
    const payload = { date, status, ...(remarks ? { remarks } : {}) };
    const response = await api.patch("/attendance/my-lop-reversal", payload);
    return response.data;
  },

  // Optimized chunked API: returns employees + their attendance in a single request
  // Perfect for infinite scroll: 100 employees + 3000 attendance records (30 days × 100)
  // Instead of 50,000 records fetched upfront
  getChunkedAttendance: async (
    month = null,
    year = null,
    page = 1,
    limit = 100,
    search = "",
    filters = {}
  ) => {
    const params = {
      page,
      limit,
      month: month || new Date().getMonth() + 1,
      year: year || new Date().getFullYear(),
      // 🚀 Request only essential fields to reduce payload size
      fields:
        "id,emp_code,name,joining_date,relieved_date,employment_status,email",
    };
    if (search) params.search = search;
    
    // ✅ CRITICAL: Always include companyId to filter by current company
    // Try multiple sources: direct localStorage key, user object, or companyData
    let companyId = getAuthItem("companyId");
    
    // Fallback 1: Try to get from currentUser object
    if (!companyId || companyId === "null" || companyId === "undefined") {
      try {
        const currentUser = getAuthItem("currentUser");
        if (currentUser) {
          const user = JSON.parse(currentUser);
          companyId = user.companyId || user.company_id;
        }
      } catch (e) {
        // Ignore parse errors
      }
    }
    
    // Fallback 2: Try to get from companyData.id (priority: id field)
    if (!companyId || companyId === "null" || companyId === "undefined") {
      try {
        const companyData = getAuthItem("companyData");
        if (companyData) {
          const company = JSON.parse(companyData);
          // Use company.id as primary source (as per user requirement)
          companyId = company.id || company.companyId;
        }
      } catch (e) {
        // Ignore parse errors
      }
    }
    
    // Only add companyId if it's a valid, non-empty value
    if (companyId && companyId !== "null" && companyId !== "undefined" && String(companyId).trim() !== "") {
      params.companyId = String(companyId).trim();
    }
    
    // Merge any additional filters (but don't override companyId if already set)
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== "undefined" && value !== "null") {
        // Don't override companyId if it was already set from localStorage
        if (key === "companyId" && params.companyId) {
          return; // Skip, keep the localStorage value
        }
        params[key] = value;
      }
    });
    // ✅ REMOVED: timestamp parameter (_t) - it prevented browser caching!
    // Now responses will be cached for 5 minutes, reducing redundant requests by 80%

    const response = await api.get("/attendance/chunked", { params });
    return response.data;
  },

  // 🚀 NEW: Load 5 chunks (500 employees) in parallel for MAXIMUM SPEED
  // Load chunks [1, 2, 3, 4, 5] simultaneously = 5x faster than sequential
  getMultipleChunkedAttendanceMax: async (
    month = null,
    year = null,
    pages = [1, 2, 3, 4, 5], // Load 5 chunks (500 employees) by default
    limit = 100,
    search = "",
    filters = {}
  ) => {
    try {
      // ✅ CRITICAL: Always include companyId to filter by current company
      // Try multiple sources: direct localStorage key, user object, or companyData
      let companyId = getAuthItem("companyId");
      
      // Fallback 1: Try to get from currentUser object
      if (!companyId || companyId === "null" || companyId === "undefined") {
        try {
          const currentUser = getAuthItem("currentUser");
          if (currentUser) {
            const user = JSON.parse(currentUser);
            companyId = user.companyId || user.company_id;
          }
        } catch (e) {
          // Ignore parse errors
        }
      }
      
      // Fallback 2: Try to get from companyData.id (priority: id field)
      if (!companyId || companyId === "null" || companyId === "undefined") {
        try {
          const companyData = getAuthItem("companyData");
          if (companyData) {
            const company = JSON.parse(companyData);
            // Use company.id as primary source (as per user requirement)
            companyId = company.id || company.companyId;
          }
        } catch (e) {
          // Ignore parse errors
        }
      }
      
      // Only use companyId if it's a valid, non-empty value
      const validCompanyId = companyId && 
        companyId !== "null" && 
        companyId !== "undefined" && 
        String(companyId).trim() !== "" 
        ? String(companyId).trim() 
        : null;
      
      // 🚀 Create 5 parallel requests (much faster than sequential!)
      const requests = pages.map((page) => {
        const requestParams = {
          page,
          limit,
          month: month || new Date().getMonth() + 1,
          year: year || new Date().getFullYear(),
          fields:
            "id,emp_code,name,joining_date,relieved_date,employment_status,email",
          search: search || undefined,
          ...filters,
        };
        
        // Only add companyId if it's valid
        if (validCompanyId) {
          requestParams.companyId = validCompanyId;
        }
        
        return api.get("/attendance/chunked", { params: requestParams });
      });

      // Wait for all 5 requests in parallel (500 employees instant!)
      const results = await Promise.all(requests);
      const data = results.map((r) => r.data);

      // Merge all results into one
      return {
        success: data.every((d) => d.success),
        employees: data.flatMap((d) => d.employees || []),
        attendance: Object.assign({}, ...data.map((d) => d.attendance || {})),
        pagination: data[data.length - 1]?.pagination || {},
      };
    } catch (err) {
      throw err.response?.data || err.message || err;
    }
  },

  // 🚀 NEW: Load multiple chunks in parallel for 3x faster data loading
  // Instead of loading chunks sequentially (100, then 200, then 300...)
  // Load chunks 2, 3, 4 in parallel while displaying chunk 1
  getMultipleChunkedAttendance: async (
    month = null,
    year = null,
    pages = [1, 2, 3], // Which page numbers to load
    limit = 100,
    search = "",
    filters = {}
  ) => {
    try {
      // ✅ CRITICAL: Always include companyId to filter by current company
      // Try multiple sources: direct localStorage key, user object, or companyData
      let companyId = getAuthItem("companyId");
      
      // Fallback 1: Try to get from currentUser object
      if (!companyId || companyId === "null" || companyId === "undefined") {
        try {
          const currentUser = getAuthItem("currentUser");
          if (currentUser) {
            const user = JSON.parse(currentUser);
            companyId = user.companyId || user.company_id;
          }
        } catch (e) {
          // Ignore parse errors
        }
      }
      
      // Fallback 2: Try to get from companyData.id (priority: id field)
      if (!companyId || companyId === "null" || companyId === "undefined") {
        try {
          const companyData = getAuthItem("companyData");
          if (companyData) {
            const company = JSON.parse(companyData);
            // Use company.id as primary source (as per user requirement)
            companyId = company.id || company.companyId;
          }
        } catch (e) {
          // Ignore parse errors
        }
      }
      
      // Only use companyId if it's a valid, non-empty value
      const validCompanyId = companyId && 
        companyId !== "null" && 
        companyId !== "undefined" && 
        String(companyId).trim() !== "" 
        ? String(companyId).trim() 
        : null;
      
      // Create parallel requests for all pages (much faster than sequential!)
      const requests = pages.map((page) => {
        const requestParams = {
          page,
          limit,
          month: month || new Date().getMonth() + 1,
          year: year || new Date().getFullYear(),
          fields:
            "id,emp_code,name,joining_date,relieved_date,employment_status,email",
          search: search || undefined,
          ...filters,
        };
        
        // Only add companyId if it's valid
        if (validCompanyId) {
          requestParams.companyId = validCompanyId;
        }
        
        return api.get("/attendance/chunked", { params: requestParams });
      });

      // Wait for all requests in parallel (3 requests simultaneously = 3x faster)
      const results = await Promise.all(requests);
      const data = results.map((r) => r.data);

      // Merge all results into one
      return {
        success: data.every((d) => d.success),
        employees: data.flatMap((d) => d.employees || []),
        attendance: Object.assign({}, ...data.map((d) => d.attendance || {})),
        pagination: data[data.length - 1]?.pagination || {},
      };
    } catch (err) {
      throw err.response?.data || err.message || err;
    }
  },

  // Backwards-compatible alias used by some components
  getMonthly: async (year = null, month = null) => {
    try {
      const params = {};
      if (month !== null && year !== null) {
        params.month = month;
        params.year = year;
      }
      // ✅ REMOVED: timestamp parameter - allows browser caching
      // Responses now cached for 5 minutes, reducing API load by 80%
      const response = await api.get("/attendance/history", { params });
      return response.data;
    } catch (err) {
      throw err.response?.data || err.message || err;
    }
  },

  getAttendanceStats: async () => {
    const response = await api.get("/attendance/stats");
    return response.data;
  },

  bulkMarkAttendance: async (attendanceData, selectedDate = null) => {
    // If caller already provided a pre-built payload matching backend format,
    // accept it directly. This allows the frontend to build records with
    // employeeId, date, status and remarks and send exactly as backend expects.
    if (
      attendanceData &&
      typeof attendanceData === "object" &&
      Array.isArray(attendanceData.employeeAttendance)
    ) {
      const response = await api.post("/attendance/bulk-mark", attendanceData);
      return response.data;
    }

    // selectedDate is "2025-11-01"
    const dateToUse = selectedDate || new Date().toISOString().slice(0, 10);

    // Build year-month prefix from date string to avoid timezone issues
    const yearMonthPrefix = dateToUse.substring(0, 8); // "2025-11-"

    const employeeAttendance = Object.entries(attendanceData).flatMap(
      ([empId, days]) =>
        Object.entries(days).map(([day, status]) => ({
          employeeId: parseInt(empId, 10),
          date: `${yearMonthPrefix}${String(day).padStart(2, "0")}`,
          status,
          remarks: "",
        }))
    );

    const transformed = {
      date: dateToUse,
      employeeAttendance,
    };

    const response = await api.post("/attendance/bulk-mark", transformed);
    return response.data;
  },

  lockAttendance: async (month, year, payload = {}) => {
    const response = await api.put(`/attendance/lock/${month}/${year}`, payload);
    return response.data;
  },

  requestUnlockAttendance: async (month, year) => {
    const response = await api.put(`/attendance/unlock-request/${month}/${year}`);
    return response.data;
  },

  unlockAttendance: async (month, year, payload = {}) => {
    const response = await api.put(`/attendance/unlock/${month}/${year}`, payload);
    return response.data;
  },

  importAttendance: async (formData) => {
    const response = await api.post("/attendance/import", formData, {
      // Let browser set multipart boundary automatically.
      // Manually forcing Content-Type can break some uploads.
      timeout: 120000,
    });
    return response.data;
  },

  exportAttendance: async (month, year) => {
    const response = await api.get(`/attendance/export/${month}/${year}`, {
      responseType: "blob",
    });
    return response;
  },

  downloadTemplate: async (month, year, employeeIds = null) => {
    const params = {};
    if (month !== undefined && month !== null) {
      // Convert 0-indexed month to 1-indexed (0-11 => 1-12)
      params.month = typeof month === "number" ? month + 1 : month;
    }
    if (year !== undefined && year !== null) {
      params.year = year;
    }
    if (Array.isArray(employeeIds) && employeeIds.length > 0) {
      params.employeeIds = employeeIds.join(",");
    }
    const response = await api.get("/attendance/template", {
      params,
      responseType: "blob",
    });
    return response;
  },
  // Attendance configuration codes (custom company mappings)
  getConfigurations: async () => {
    const response = await api.get("/attendance/configurations");
    const d = response.data;
    // Return full object when present so halfDayDivider/halfDayPrefix are preserved; else inner data or []
    if (d && typeof d === "object" && !Array.isArray(d)) return d;
    return d?.data ?? [];
  },

  saveConfigurations: async (configs) => {
    // Accept either an array of configs or an object with { codes: [...], halfDayDivider?, halfDayPrefix? }
    // Backend expects: { codes: [ { meaning, customCode, importCode, exportCode } ], halfDayDivider?, halfDayPrefix? } (company-wise)
    let body;
    if (Array.isArray(configs)) {
      body = {
        codes: configs.map((c) => ({
          meaning: c.meaning,
          customCode: c.customCode,
          importCode: c.importCode,
          exportCode: c.exportCode,
        })),
      };
    } else if (configs && typeof configs === "object") {
      body = configs;
    } else {
      body = { codes: [] };
    }

    const response = await api.post("/attendance/configurations", body);
    const d = response.data;
    if (d && typeof d === "object" && !Array.isArray(d)) return d;
    return d?.data ?? [];
  },

  resetConfigurations: async () => {
    const response = await api.post("/attendance/configurations/reset");
    const d = response.data;
    if (d && typeof d === "object" && !Array.isArray(d)) return d;
    return d?.data ?? [];
  },
};

// Payroll services
const resolveCompanyIdFromStorage = () => {
  let companyId = localStorage.getItem("companyId");

  if (!companyId || companyId === "null" || companyId === "undefined") {
    try {
      const currentUser = localStorage.getItem("currentUser");
      if (currentUser) {
        const user = JSON.parse(currentUser);
        companyId = user.companyId || user.company_id;
      }
    } catch (e) {
      // Ignore parse errors
    }
  }

  if (!companyId || companyId === "null" || companyId === "undefined") {
    try {
      const companyData = localStorage.getItem("companyData");
      if (companyData) {
        const company = JSON.parse(companyData);
        companyId = company.id || company.companyId;
      }
    } catch (e) {
      // Ignore parse errors
    }
  }

  if (
    companyId &&
    companyId !== "null" &&
    companyId !== "undefined" &&
    String(companyId).trim() !== ""
  ) {
    return String(companyId).trim();
  }

  return null;
};

export const payrollService = {
  generatePayslip: async (payrollData) => {
    const response = await api.post("/payroll/generate", payrollData);
    return response.data;
  },

  getPayslips: async (employeeId) => {
    const response = await api.get(`/payroll/payslips/${employeeId}`);
    return response.data;
  },

  submitTaxDeclaration: async (declarationData) => {
    const response = await api.post(
      "/payroll/tax-declaration",
      declarationData
    );
    return response.data;
  },

  getTaxDeclaration: async (employeeId) => {
    const response = await api.get(`/payroll/tax-declaration/${employeeId}`);
    return response.data;
  },

  updateTaxDeclarationStatus: async (id, status) => {
    const response = await api.put(`/payroll/tax-declaration/${id}`, {
      status,
    });
    return response.data;
  },

  bulkImportDeclarations: async (formData) => {
    const response = await api.post(
      "/payroll/declarations/bulk-import",
      formData,
      {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      }
    );
    return response.data;
  },

  exportDeclarations: async (financialYear, taxRegime = null) => {
    const params = { financialYear };
    if (taxRegime) {
      params.taxRegime = taxRegime;
    }
    // Defense-in-depth: also send companyId so export stays tenant-scoped
    // even if JWT company claim is missing on some sessions.
    const companyId =
      getAuthItem("companyId") ||
      (() => {
        try {
          const user = JSON.parse(getAuthItem("currentUser") || "null");
          return user?.companyId || user?.company_id || null;
        } catch {
          return null;
        }
      })() ||
      (() => {
        try {
          const company = JSON.parse(getAuthItem("companyData") || "null");
          return company?.id || null;
        } catch {
          return null;
        }
      })();
    if (companyId) params.companyId = companyId;

    const response = await api.get("/payroll/declarations/export", {
      params,
      responseType: "blob",
    });
    return response;
  },

  bulkApproveDeclarations: async (declarationIds) => {
    const response = await api.put("/payroll/declarations/bulk-approve", {
      declarationIds,
    });
    return response.data;
  },

  downloadDeclarationTemplate: async () => {
    const response = await api.get("/payroll/declarations/template", {
      responseType: "blob",
    });
    return response;
  },

  // Investment declaration headers (tax regime + status) for all employees in a year
  getInvestmentHeaders: async (financialYear) => {
    const response = await api.get("/payroll/investment-headers", {
      params: { financialYear },
    });
    return response.data;
  },

  // Normalized investment declaration (header + sections + attachments)
  getInvestmentData: async (employeeId, financialYear) => {
    const params = financialYear
      ? `?financialYear=${encodeURIComponent(financialYear)}`
      : "";
    const response = await api.get(
      `/payroll/investment-data/${employeeId}${params}`
    );
    return response.data;
  },

  saveInvestmentData: async (employeeId, investmentData) => {
    const response = await api.post(
      `/payroll/investment-data/${employeeId}`,
      investmentData
    );
    return response.data;
  },

  deleteInvestmentData: async (employeeId, financialYear) => {
    const response = await api.delete(
      `/payroll/investment-data/${employeeId}`,
      {
        params: { financialYear },
      }
    );
    return response.data;
  },

  // Proof upload and attachments
  uploadProof: async (file, options = {}) => {
    const formData = new FormData();
    formData.append("file", file);

    // R2 naming hints so backend can use deterministic per-employee, per-slot keys
    const { employeeId, slotKey, storageFileName } = options || {};
    if (employeeId != null) {
      formData.append("employeeId", employeeId);
    }
    if (slotKey) {
      formData.append("slotKey", slotKey);
    }
    if (storageFileName) {
      formData.append("storageFileName", storageFileName);
    }

    const response = await api.post("/payroll/upload-proof", formData);
    return response.data; // expects { success, data: { fileUrl, fileName, fileSize, ... } }
  },

  createInvestmentAttachment: async (declarationId, payload) => {
    const response = await api.post(
      `/payroll/investment-attachments/${declarationId}`,
      payload
    );
    return response.data;
  },

  deleteInvestmentAttachment: async (attachmentId) => {
    const response = await api.delete(
      `/payroll/investment-attachments/${attachmentId}`
    );
    return response.data;
  },

  reviewInvestmentAttachment: async (attachmentId, { status, comment }) => {
    const response = await api.patch(
      `/payroll/investment-attachments/${attachmentId}`,
      { status, comment }
    );
    return response.data;
  },

  getInvestmentAttachmentUrl: async (attachmentId) => {
    const response = await api.get(
      `/payroll/investment-attachments/${attachmentId}/url`
    );
    return response.data;
  },

  getInvestmentAttachmentDownloadUrl: async (attachmentId) => {
    const response = await api.get(
      `/payroll/investment-attachments/${attachmentId}/download-url`
    );
    return response.data;
  },

  // Payroll settings
  getPayrollSettings: async () => {
    const companyId = resolveCompanyIdFromStorage();
    const params = companyId ? { companyId } : {};
    const response = await api.get("/payroll/settings", { params });
    return response.data;
  },

  upsertPayrollSettings: async (settingsData) => {
    const companyId = resolveCompanyIdFromStorage();
    const payload = companyId
      ? { ...settingsData, companyId }
      : settingsData;
    const response = await api.put("/payroll/settings", payload);
    return response.data;
  },
};

// Investment services
export const investmentService = {
  getInvestments: async (employeeId) => {
    // Follows the same standard as other services
    const response = await api.get(`/investments/${employeeId}`);
    return response.data;
  },
};

// Department services
export const departmentService = {
  createDepartment: async (departmentData) => {
    const response = await api.post("/departments", departmentData);
    return response.data;
  },

  getDepartments: async (includeInactive = false) => {
    const params = includeInactive ? { includeInactive: "true" } : {};
    const response = await api.get("/departments", { params });
    return response.data;
  },

  getDepartment: async (id) => {
    const response = await api.get(`/departments/${id}`);
    return response.data;
  },

  updateDepartment: async (id, departmentData) => {
    const response = await api.put(`/departments/${id}`, departmentData);
    return response.data;
  },

  deleteDepartment: async (id) => {
    const response = await api.delete(`/departments/${id}`);
    return response.data;
  },
};

export const subDepartmentService = {
  createSubDepartment: async (subDepartmentData) => {
    const response = await api.post("/sub-departments", subDepartmentData);
    return response.data;
  },

  getAllSubDepartments: async (includeInactive = false) => {
    const params = includeInactive ? { includeInactive: "true" } : {};
    const response = await api.get("/sub-departments", { params });
    return response.data;
  },

  getSubDepartment: async (id) => {
    const response = await api.get(`/sub-departments/${id}`);
    return response.data;
  },

  updateSubDepartment: async (id, subDepartmentData) => {
    const response = await api.put(`/sub-departments/${id}`, subDepartmentData);
    return response.data;
  },

  deleteSubDepartment: async (id) => {
    const response = await api.delete(`/sub-departments/${id}`);
    return response.data;
  },
};

// Designation services
export const designationService = {
  createDesignation: async (designationData) => {
    const response = await api.post("/designations", designationData);
    return response.data;
  },

  getDesignations: async (includeInactive = false) => {
    const params = includeInactive ? { includeInactive: "true" } : {};
    const response = await api.get("/designations", { params });
    return response.data;
  },

  getDesignation: async (id) => {
    const response = await api.get(`/designations/${id}`);
    return response.data;
  },

  updateDesignation: async (id, designationData) => {
    const response = await api.put(`/designations/${id}`, designationData);
    return response.data;
  },

  deleteDesignation: async (id) => {
    const response = await api.delete(`/designations/${id}`);
    return response.data;
  },
};

// Branch services
export const branchService = {
  createBranch: async (branchData) => {
    const response = await api.post("/branches", branchData);
    return response.data;
  },

  getBranches: async (includeInactive = false) => {
    const params = includeInactive ? { includeInactive: "true" } : {};
    const response = await api.get("/branches", { params });
    return response.data;
  },

  getBranch: async (id) => {
    const response = await api.get(`/branches/${id}`);
    return response.data;
  },

  updateBranch: async (id, branchData) => {
    const response = await api.put(`/branches/${id}`, branchData);
    return response.data;
  },

  deleteBranch: async (id) => {
    const response = await api.delete(`/branches/${id}`);
    return response.data;
  },
};

// Grades services
export const gradesService = {
  createGrade: async (gradeData) => {
    const response = await api.post("/grades", gradeData);
    return response.data;
  },

  getGrades: async (includeInactive = false) => {
    const params = includeInactive ? { includeInactive: "true" } : {};
    const response = await api.get("/grades", { params });
    return response.data;
  },

  updateGrade: async (id, gradeData) => {
    const response = await api.put(`/grades/${id}`, gradeData);
    return response.data;
  },

  deleteGrade: async (id) => {
    const response = await api.delete(`/grades/${id}`);
    return response.data;
  },
};

export const categoryService = {
  createCategory: async (categoryData) => {
    const response = await api.post("/categories", categoryData);
    return response.data;
  },

  getAllCategories: async (includeInactive = false) => {
    const params = includeInactive ? { includeInactive: "true" } : {};
    const response = await api.get("/categories", { params });
    return response.data;
  },

  updateCategory: async (id, categoryData) => {
    const response = await api.put(`/categories/${id}`, categoryData);
    return response.data;
  },

  deleteCategory: async (id) => {
    const response = await api.delete(`/categories/${id}`);
    return response.data;
  },
};

export const reasonsService = {
  createReason: async (reasonData) => {
    const response = await api.post("/reasons", reasonData);
    return response.data;
  },

  getAllTerminationReasons: async (includeInactive = false) => {
    const params = includeInactive ? { includeInactive: "true" } : {};
    const response = await api.get("/reasons/termination", { params });
    return response.data;
  },

  getAllResignationReasons: async (includeInactive = false) => {
    const params = includeInactive ? { includeInactive: "true" } : {};
    const response = await api.get("/reasons/resignation", { params });
    return response.data;
  },

  updateReason: async (id, reasonData) => {
    const response = await api.put(`/reasons/${id}`, reasonData);
    return response.data;
  },

  deleteReason: async (id) => {
    const response = await api.delete(`/reasons/${id}`);
    return response.data;
  },
};

// Employee Code Structure services
export const employeeCodeStructureService = {
  getCurrentStructure: async () => {
    const response = await api.get("/employee-code-structure/current");
    return response.data;
  },

  getStructureHistory: async () => {
    const response = await api.get("/employee-code-structure/history");
    return response.data;
  },

  saveStructure: async (structureData) => {
    const response = await api.post(
      "/employee-code-structure/save",
      structureData
    );
    return response.data;
  },

  generatePreview: async (previewData) => {
    const response = await api.post(
      "/employee-code-structure/preview",
      previewData
    );
    return response.data;
  },

  getNextEmployeeCode: async () => {
    const response = await api.get("/employee-code-structure/next");
    return response.data;
  },

  updateCurrentCount: async (countData) => {
    const response = await api.put(
      "/employee-code-structure/update-count",
      countData
    );
    return response.data;
  },
};

// Confirmation services
export const confirmationService = {
  createConfirmation: async (confirmationData) => {
    const response = await api.post("/confirmations", confirmationData);
    return response.data;
  },

  getConfirmationHistory: async (employeeId) => {
    const response = await api.get(`/confirmations/history/${employeeId}`);
    return response.data;
  },

  getConfirmation: async (id) => {
    const response = await api.get(`/confirmations/${id}`);
    return response.data;
  },
};

// Transfer services
export const transferService = {
  createTransfer: async (transferData) => {
    const response = await api.post("/transfers", transferData);
    return response.data;
  },

  getTransferHistory: async (employeeId) => {
    const response = await api.get(`/transfers/history/${employeeId}`);
    return response.data;
  },

  getTransfer: async (id) => {
    const response = await api.get(`/transfers/${id}`);
    return response.data;
  },
};

// Salary services
export const salaryService = {
  createSalary: async (salaryData) => {
    const response = await api.post("/salaries", salaryData);
    return response.data;
  },

  getSalaryHistory: async (employeeId) => {
    const response = await api.get(`/salaries/history/${employeeId}`);
    return response.data;
  },

  getSalary: async (id) => {
    const response = await api.get(`/salaries/${id}`);
    return response.data;
  },
};

// Resignation services
export const resignationService = {
  createResignation: async (resignationData) => {
    const response = await api.post("/resignations", resignationData);
    return response.data;
  },

  getResignationHistory: async (employeeId) => {
    const response = await api.get(`/resignations/history/${employeeId}`);
    return response.data;
  },

  getResignation: async (id) => {
    const response = await api.get(`/resignations/${id}`);
    return response.data;
  },
};

// Attendance Structure services
export const attendanceStructureService = {
  // Weekly Off Policies
  getWeeklyOffPolicies: async () => {
    const response = await api.get("/attendance-structure/weekly-off-policies");
    return response.data;
  },

  createWeeklyOffPolicy: async (policyData) => {
    const response = await api.post(
      "/attendance-structure/weekly-off-policies",
      policyData
    );
    return response.data;
  },

  updateWeeklyOffPolicy: async (id, policyData) => {
    const response = await api.put(
      `/attendance-structure/weekly-off-policies/${id}`,
      policyData
    );
    return response.data;
  },

  deleteWeeklyOffPolicy: async (id) => {
    const response = await api.delete(
      `/attendance-structure/weekly-off-policies/${id}`
    );
    return response.data;
  },

  // Holiday Policies
  getHolidayPolicies: async () => {
    const response = await api.get("/attendance-structure/holiday-policies");
    return response.data;
  },

  createHolidayPolicy: async (policyData) => {
    const response = await api.post(
      "/attendance-structure/holiday-policies",
      policyData
    );
    return response.data;
  },

  updateHolidayPolicy: async (id, policyData) => {
    const response = await api.put(
      `/attendance-structure/holiday-policies/${id}`,
      policyData
    );
    return response.data;
  },

  deleteHolidayPolicy: async (id) => {
    const response = await api.delete(
      `/attendance-structure/holiday-policies/${id}`
    );
    return response.data;
  },

  // Holidays
  getHolidays: async () => {
    const response = await api.get("/attendance-structure/holidays");
    return response.data;
  },

  createHoliday: async (holidayData) => {
    const response = await api.post(
      "/attendance-structure/holidays",
      holidayData
    );
    return response.data;
  },

  // Holiday Bank
  getHolidayBank: async () => {
    const response = await api.get("/attendance-structure/holiday-bank");
    return response.data;
  },

  createHolidayBank: async (holidayData) => {
    const response = await api.post(
      "/attendance-structure/holiday-bank",
      holidayData
    );
    return response.data;
  },

  deleteHolidayBank: async (id) => {
    const response = await api.delete(
      `/attendance-structure/holiday-bank/${id}`
    );
    return response.data;
  },

  // Leave Types
  getLeaveTypes: async () => {
    const response = await api.get("/attendance-structure/leave-types");
    return response.data;
  },

  createLeaveType: async (leaveTypeData) => {
    const response = await api.post(
      "/attendance-structure/leave-types",
      leaveTypeData
    );
    return response.data;
  },

  updateLeaveType: async (id, leaveTypeData) => {
    const response = await api.put(
      `/attendance-structure/leave-types/${id}`,
      leaveTypeData
    );
    return response.data;
  },

  deleteLeaveType: async (id) => {
    const response = await api.delete(
      `/attendance-structure/leave-types/${id}`
    );
    return response.data;
  },

  // Leave Groups
  getLeaveGroups: async () => {
    const response = await api.get("/attendance-structure/leave-groups");
    return response.data;
  },

  createLeaveGroup: async (leaveGroupData) => {
    const response = await api.post(
      "/attendance-structure/leave-groups",
      leaveGroupData
    );
    return response.data;
  },

  updateLeaveGroup: async (id, leaveGroupData) => {
    const response = await api.put(
      `/attendance-structure/leave-groups/${id}`,
      leaveGroupData
    );
    return response.data;
  },

  deleteLeaveGroup: async (id) => {
    const response = await api.delete(
      `/attendance-structure/leave-groups/${id}`
    );
    return response.data;
  },

  // Leave Adjustment: create adjustment and fetch history
  createLeaveAdjustment: async (payload) => {
    const response = await api.post(
      "/attendance-structure/leave-adjustments",
      payload
    );
    const data = response.data;
    if (data?.success === false) {
      throw new Error(data.message || "Failed to save leave adjustment");
    }
    return data;
  },
  getLeaveAdjustmentHistory: async (employeeId) => {
    const response = await api.get("/attendance-structure/leave-adjustments", {
      params: { employeeId },
    });
    const data = response.data;
    if (data?.success === false) {
      throw new Error(data.message || "Failed to load adjustment history");
    }
    return data;
  },

  // All company leave types applicable to this employee (gender-filtered)
  getEmployeeLeaveTypes: async (employeeId) => {
    const response = await api.get("/attendance-structure/employee-leave-types", {
      params: { employeeId },
    });
    const data = response.data;
    if (data?.success === false) {
      throw new Error(data.message || "Failed to load employee leave types");
    }
    return data;
  },

  // Leave Balance: fetch balance summary for employee, year, leave type
  getLeaveBalance: async (employeeId, year, leaveTypeId) => {
    const response = await api.get("/attendance-structure/leave-balance", {
      params: { employeeId, year, leaveTypeId },
    });
    const data = response.data;
    if (data?.success === false) {
      throw new Error(data.message || "Failed to load leave balance");
    }
    return data;
  },

  getLeaveOpeningBalancesForGroup: async (leaveGroupId, year) => {
    const response = await api.get("/attendance-structure/leave-opening-balance", {
      params: { leaveGroupId, year },
    });
    return response.data;
  },

  // Upload leave opening balance for a leave group
  uploadLeaveOpeningBalance: async (leaveGroupId, entries, year) => {
    const response = await api.post(
      "/attendance-structure/leave-opening-balance/upload",
      {
        leaveGroupId,
        entries,
        year,
      }
    );
    return response.data;
  },

  getLeaveGroupEmployees: async (leaveGroupId) => {
    const response = await api.get(
      `/attendance-structure/leave-groups/${leaveGroupId}/employees`
    );
    return response.data;
  },

  cleanupLeaveOpeningBalanceByGender: async (leaveGroupId, year) => {
    const response = await api.post(
      "/attendance-structure/leave-opening-balance/cleanup-gender",
      { leaveGroupId, year }
    );
    return response.data;
  },

  createLeaveRequest: async (payload) => {
    const response = await api.post("/attendance-structure/leave-requests", payload);
    return response.data;
  },

  getLeaveRequests: async (params = {}) => {
    const response = await api.get("/attendance-structure/leave-requests", { params });
    return response.data;
  },

  getPendingLeaveRequestsForUser: async (params = {}) => {
    const response = await api.get(
      "/attendance-structure/leave-requests/pending-my-action",
      { params }
    );
    return response.data;
  },

  approveLeaveRequest: async (id, comments = "") => {
    const response = await api.post(
      `/attendance-structure/leave-requests/${id}/approve`,
      { comments }
    );
    return response.data;
  },

  rejectLeaveRequest: async (id, comments = "") => {
    const response = await api.post(
      `/attendance-structure/leave-requests/${id}/reject`,
      { comments }
    );
    return response.data;
  },

  cancelLeaveRequest: async (id) => {
    const response = await api.post(
      `/attendance-structure/leave-requests/${id}/cancel`
    );
    return response.data;
  },

  // Attendance Locks
  getAttendanceLocks: async () => {
    const response = await api.get("/attendance-structure/attendance-locks");
    return response.data;
  },

  /**
   * GET /attendance-structure/my-attendance-history?month=1&year=2026
   * Returns attendance for the logged-in employee only (employee portal).
   * Same pattern as attendance-locks; use in employee attendance calendar.
   */
  getMyAttendanceHistory: async (month, year) => {
    const response = await api.get(
      "/attendance-structure/my-attendance-history",
      { params: { month, year } }
    );
    return response.data;
  },

  // Employee Policy Mappings
  getEmployeeWeeklyOffPolicies: async () => {
    const response = await api.get(
      "/attendance-structure/employee-weekly-off-policies"
    );
    return response.data;
  },

  getEmployeeHolidayPolicies: async () => {
    const response = await api.get(
      "/attendance-structure/employee-holiday-policies"
    );
    return response.data;
  },

  getEmployeeLeaveGroups: async () => {
    const response = await api.get(
      "/attendance-structure/employee-leave-groups"
    );
    return response.data;
  },
};

// Separation Policy services
export const separationPolicyService = {
  getSeparationPolicies: async () => {
    const response = await api.get("/separation-policies");
    return response.data;
  },

  createSeparationPolicy: async (policyData) => {
    const response = await api.post("/separation-policies", policyData);
    return response.data;
  },

  updateSeparationPolicy: async (id, policyData) => {
    const response = await api.put(`/separation-policies/${id}`, policyData);
    return response.data;
  },

  deactivateSeparationPolicy: async (id) => {
    const response = await api.patch(`/separation-policies/${id}/deactivate`);
    return response.data;
  },

  deleteSeparationPolicy: async (id) => {
    const response = await api.delete(`/separation-policies/${id}`);
    return response.data;
  },
};

// Error handling wrapper
const handleApiError = (error) => {
  // If the error has a detailed response object from our server,
  // it's a validation error. Throw the ORIGINAL error so the component can see the details.
  if (error.response && error.response.data) {
    throw error;
  }

  // For any other kind of error (like a network failure), create a simple message.
  const errorMessage = error.message || "An unexpected network error occurred.";
  throw new Error(errorMessage);
};

// Add error handling to all service methods
Object.values([
  employeeService,
  attendanceService,
  payrollService,
  branchService,
  departmentService,
  designationService,
  subDepartmentService,
  gradesService,
  categoryService,
  reasonsService,
  employeeCodeStructureService,
  confirmationService,
  transferService,
  salaryService,
  resignationService,
  attendanceStructureService,
]).forEach((service) => {
  Object.keys(service).forEach((key) => {
    const originalMethod = service[key];
    service[key] = async (...args) => {
      try {
        return await originalMethod(...args);
      } catch (error) {
        handleApiError(error);
      }
    };
  });
});

export default api;
